import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgxSpinnerService } from 'ngx-spinner';
import { HttpErrorResponse } from '@angular/common/http';
import { ModalpopService } from '../modalpop.service';
import { AlertBoxComponent } from '../alert-box/alert-box.component';
import { UmsService } from '../ums.service';
import * as moment from 'moment';

@Component({
  selector: 'app-theory-exam-appointment-report',
  templateUrl: './theory-exam-appointment-report.component.html',
  styleUrls: ['./theory-exam-appointment-report.component.scss']
})
export class TheoryExamAppointmentReportComponent implements OnInit {
  reportForm: FormGroup;
  sessionList: any[] = [];
  facultyList: any[] = [];
  courseLevelList: any[] = [];
  courseList: any[] = [];
  filteredCourseList: any[] = [];
  filteredExamList: any[] = []; 
  examList: any[] = [];
  collegeList: any[] = [];
  roleList: any[] = [];

  appointmentsList: any[] = [];
  showTable: boolean = false;
  currentDateTime: Date = new Date();
  private examCache = new Map<string, any[]>();

  constructor(
    private spinner: NgxSpinnerService,
    private fb: FormBuilder,
    private umsService: UmsService,
    private modalPopupService: ModalpopService
  ) { }

  ngOnInit(): void {
    this.initializeForm();
    this.fetchSession();
    this.fetchRoles();
    this.fetchCourseLevel();
  }

  initializeForm() {
    this.reportForm = this.fb.group({
      SessionId: ['', Validators.required],
      FacultyId: [[]],
      CourseLevel: [[]],          
      Course: [[]],
      Exam: [[]],
      Center: ['', Validators.required],
      Role: [''],
      ApptType: ['R']
    });
  }

  fetchSession() {
    this.spinner.show();
    const payload = { SnDefault: '', SnNo: '' };
    this.umsService.getSession(payload).subscribe(res => {
      this.spinner.hide();
      if (res.status) {
        this.sessionList = res.data.filter((el: any) => el.SnStatus === 'O');
      }
    }, (error: HttpErrorResponse) => {
      this.spinner.hide();
      this.sessionList = [];
    });
  }

  fetchCourseLevel() {
    const payload = { CdType: 'COLVL' };
    this.umsService.getCodeType(payload).subscribe(res => {
      if (res.status && res.data) {
        this.courseLevelList = res.data;
      } else {
        this.courseLevelList = [];
      }
    }, (error) => {
      this.courseLevelList = [];
    });
  }

  fetchRoles() {
    const payload = { CdType: 'ReqTy' };  
    this.umsService.getCodeType(payload).subscribe(res => {
      if (res.status && res.data) {
        const allowedRoleCodes = ['CI', 'CO'];
        this.roleList = res.data.filter((role: any) => 
          role.CdActive === 'Y' && allowedRoleCodes.includes(role.CdCode)
        );
      } else {
        this.roleList = [];
      }
    }, (error: HttpErrorResponse) => {
      this.roleList = [];
      console.error('Error fetching roles:', error);
    });
  }

  onSessionChange(sessionId: number) {
    this.spinner.show();
    this.resetDependentFields(['FacultyId', 'CourseLevel', 'Course', 'Exam', 'Center']);
    this.appointmentsList = [];
    this.showTable = false;
    this.examList = [];
    this.examCache.clear();
    
    const payload = { SessionId: sessionId };
    this.umsService.getFaculty(payload).subscribe(res => {
      this.spinner.hide();
      if (res.status && res.data) {
        this.facultyList = res.data;
      } else {
        this.facultyList = [];
      }
    }, (error: HttpErrorResponse) => {
      this.spinner.hide();
      this.facultyList = [];
    });
    this.loadCentersBySession(sessionId);
  }

  loadCentersBySession(sessionId: number) {
    const payload = { SessionId: sessionId };
    this.umsService.getCentresFromAllocation(payload).subscribe({
      next: (res) => {
        if (res.status && res.data && res.data.length > 0) {
          this.collegeList = res.data.map((item: any) => ({
            CgCode: item.CgCode,
            CgName: item.CgName,
            DistrictName: item.DistrictName
          }));
        } else {
          this.collegeList = [];
        }
      },
      error: (error) => {
        this.collegeList = [];
        console.error('Error fetching centers:', error);
      }
    });
  }

  onFacultyChange(selectedFaculties: any[]) {
    this.spinner.show();
    this.resetDependentFields(['CourseLevel', 'Course', 'Exam']);
    this.appointmentsList = [];
    this.showTable = false;
    this.examList = [];
    this.examCache.clear();

    const sessionId = this.reportForm.get('SessionId')?.value;
    if (!sessionId || !selectedFaculties || selectedFaculties.length === 0) {
      this.courseList = [];
      this.filteredCourseList = [];
      this.spinner.hide();
      return;
    }

    // Handle "Select All" toggling
    let actualFaculties = selectedFaculties.filter(f => f !== 'selectAll');
    if (selectedFaculties.includes('selectAll')) {
      const all = this.facultyList.map(f => f.FcCode);
      const allSelected = actualFaculties.length === all.length && all.every(c => actualFaculties.includes(c));
      actualFaculties = allSelected ? [] : all;
      this.reportForm.patchValue({ FacultyId: actualFaculties }, { emitEvent: false });
      if (actualFaculties.length === 0) {
        this.courseList = [];
        this.filteredCourseList = [];
        this.spinner.hide();
        return;
      }
    }

    const facultyCodes = actualFaculties.join(',');
    const payload = { 
      SessionId: sessionId,
      FcCode: facultyCodes 
    };
    
    this.umsService.getCoursesWithLevel(payload).subscribe({
      next: (res) => {
        this.spinner.hide();
        if (res.status && res.data && res.data.length > 0) {
          this.courseList = res.data;
          const currentLevels = this.reportForm.get('CourseLevel')?.value || [];
          if (currentLevels.length > 0) {
            this.filteredCourseList = this.courseList.filter(course =>
              currentLevels.includes(course.CourseLevel?.toString().trim() || '')
            );
          } else {
            this.filteredCourseList = [...this.courseList];
          }
          this.reportForm.patchValue({ Course: [] });
        } else {
          this.courseList = [];
          this.filteredCourseList = [];
        }
        if (this.reportForm.get('Center')?.value) {
          this.fetchAppointments();
        }
      },
      error: (error: HttpErrorResponse) => {
        this.spinner.hide();
        this.courseList = [];
        this.filteredCourseList = [];
        console.error('Error fetching courses:', error);
      }
    });
  }

  onCourseLevelChange(selectedLevels: string[]) {
    const hasSelectAll = selectedLevels && selectedLevels.includes('selectAll');
    let actualLevels = selectedLevels ? selectedLevels.filter(l => l !== 'selectAll') : [];

    if (hasSelectAll) {
      const all = this.courseLevelList.map(item => item.CdCode);
      const allSelected = actualLevels.length === all.length && all.every(c => actualLevels.includes(c));
      const newSelection = allSelected ? [] : all;
      this.reportForm.patchValue({ CourseLevel: newSelection }, { emitEvent: false });
      actualLevels = newSelection;
    }

    if (!actualLevels || actualLevels.length === 0) {
      this.filteredCourseList = [...this.courseList];
    } else {
      this.filteredCourseList = this.courseList.filter(course => {
        const courseLevel = course.CourseLevel?.toString().trim() || '';
        return actualLevels.includes(courseLevel);
      });
    }

    this.reportForm.patchValue({ Course: [] });
    this.resetDependentFields(['Exam']);
    this.appointmentsList = [];
    this.showTable = false;
    this.examList = [];
    this.filteredExamList = [];
    this.examCache.clear();

    if (this.reportForm.get('Center')?.value) {
      this.fetchAppointments();
    }
  }

  onCourseChange(courseCodes: string[]) {
    const hasSelectAll = courseCodes && courseCodes.includes('selectAll');
    let actualCodes = courseCodes ? courseCodes.filter(c => c !== 'selectAll') : [];

    if (hasSelectAll) {
      const all = this.filteredCourseList.map(item => item.CoCgCoCode);
      const allSelected = actualCodes.length === all.length && all.every(c => actualCodes.includes(c));
      const newSelection = allSelected ? [] : all;
      this.reportForm.patchValue({ Course: newSelection }, { emitEvent: false });

      if (newSelection.length === 0) {
        this.resetDependentFields(['Exam']);
        this.appointmentsList = [];
        this.showTable = false;
        this.examList = [];
        this.filteredExamList = [];   
        if (this.reportForm.get('Center')?.value) {
          this.fetchAppointments();
        }
        return;
      } else {
        this.spinner.show();
        this.resetDependentFields(['Exam']);
        this.appointmentsList = [];
        this.showTable = false;
        
        const selectedCourses = newSelection.join(',');
        const cacheKey = selectedCourses;
        
        if (this.examCache.has(cacheKey)) {
          this.examList = this.examCache.get(cacheKey)!;
          this.filteredExamList = [...this.examList];
          this.spinner.hide();
          if (this.reportForm.get('Center')?.value) {
            this.fetchAppointments();
          }
          return;
        }

        const examPayload = {
          EmCoCode: selectedCourses,
          EmId: 0,
          CgCode: '',
          SearchString: '',
          PageSize: 1000,
          PageNumber: 1
        };
        this.umsService.getExam(examPayload).subscribe({
          next: (res) => {
            this.spinner.hide();
            if (res.status && res.data && res.data.length > 0) {
              this.examList = res.data;
              this.filteredExamList = [...res.data];
              this.examCache.set(cacheKey, res.data);
            } else {
              this.examList = [];
              this.filteredExamList = [];
            }
            if (this.reportForm.get('Center')?.value) {
              this.fetchAppointments();
            }
          },
          error: (error) => {
            this.spinner.hide();
            this.examList = [];
            this.filteredExamList = [];
            console.error('Error fetching exams:', error);
          }
        });
        return;
      }
    }

    if (!courseCodes || courseCodes.length === 0) {
      this.resetDependentFields(['Exam']);
      this.appointmentsList = [];
      this.showTable = false;
      this.examList = [];
      this.filteredExamList = [];   
      if (this.reportForm.get('Center')?.value) {
        this.fetchAppointments();
      }
      return;
    }

    this.spinner.show();
    this.resetDependentFields(['Exam']);
    this.appointmentsList = [];
    this.showTable = false;
    
    const selectedCourses = courseCodes.join(',');
    const cacheKey = selectedCourses;
    
    if (this.examCache.has(cacheKey)) {
      this.examList = this.examCache.get(cacheKey)!;
      this.filteredExamList = [...this.examList];
      this.spinner.hide();
      if (this.reportForm.get('Center')?.value) {
        this.fetchAppointments();
      }
      return;
    }

    const examPayload = {
      EmCoCode: selectedCourses,
      EmId: 0,
      CgCode: '',
      SearchString: '',
      PageSize: 1000,
      PageNumber: 1
    };
    this.umsService.getExam(examPayload).subscribe({
      next: (res) => {
        this.spinner.hide();
        if (res.status && res.data && res.data.length > 0) {
          this.examList = res.data;
          this.filteredExamList = [...res.data];
          this.examCache.set(cacheKey, res.data);
        } else {
          this.examList = [];
          this.filteredExamList = [];
        }
        if (this.reportForm.get('Center')?.value) {
          this.fetchAppointments();
        }
      },
      error: (error) => {
        this.spinner.hide();
        this.examList = [];
        this.filteredExamList = [];
        console.error('Error fetching exams:', error);
      }
    });
  }

  onExamChange(examIds: any[]) {
    const hasSelectAll = examIds && examIds.includes('selectAll');
    let actualIds = examIds ? examIds.filter(id => id !== 'selectAll') : [];

    if (hasSelectAll) {
      const all = this.filteredExamList.map(item => item.EmId);   
      const allSelected = actualIds.length === all.length && all.every(id => actualIds.includes(id));
      const newSelection = allSelected ? [] : all;

      this.reportForm.patchValue({ Exam: newSelection }, { emitEvent: false });

      if (newSelection.length === 0) {
        this.appointmentsList = [];
        this.showTable = false;
      }
      if (this.reportForm.get('Center')?.value) {
        this.fetchAppointments();
      }
      return;
    }
    if (!examIds || examIds.length === 0) {
      this.appointmentsList = [];
      this.showTable = false;
    }
    if (this.reportForm.get('Center')?.value) {
      this.fetchAppointments();
    }
  }

  onCenterChange() {
    this.fetchAppointments();
  }

  onRoleChange() {
    this.fetchAppointments();
  }

  onApptTypeChange() {
    this.fetchAppointments();
  }

  fetchAppointments() {
    const formValue = this.reportForm.value;
    if (!formValue.SessionId || !formValue.Center) {
      this.appointmentsList = [];
      this.showTable = false;
      return;
    }

    this.spinner.show();

    const selectedCourses = formValue.Course || [];
    const selectedExams = formValue.Exam || [];
    const selectedFaculties = formValue.FacultyId || [];
    const selectedLevels = formValue.CourseLevel || [];

    const payload: any = {
      SessionId: formValue.SessionId,
      CenterCode: formValue.Center
    };

    if (selectedFaculties.length > 0) {
      payload.FacultyId = selectedFaculties.join(',');
    }

    if (selectedLevels.length > 0) {
      payload.CourseLevel = selectedLevels.join(',');
    }

    if (selectedCourses.length > 0) {
      payload.CourseCode = selectedCourses.join(',');
    }
    if (selectedExams.length > 0) {
      payload.ExamId = selectedExams.join(',');
    }
    if (formValue.Role) {
      payload.RoleCode = formValue.Role;
    }
    if (formValue.ApptType) {
      payload.ApptType = formValue.ApptType;
    }

    console.log('📤 Fetching appointments payload:', payload);

    this.umsService.getTeacherAppointments(payload).subscribe({
      next: (res: any) => {
        this.spinner.hide();
        console.log('📥 Full API Response:', res);
        
        if (res.status && res.data && res.data.length > 0) {
          this.appointmentsList = res.data;
          this.showTable = true;
          console.log(' Appointments loaded:', this.appointmentsList.length);
        } else {
          this.appointmentsList = [];
          this.showTable = true;
          console.warn(' No appointments found');
        }
      },
      error: (error: HttpErrorResponse) => {
        this.spinner.hide();
        this.appointmentsList = [];
        this.showTable = true;
        console.error(' Error fetching appointments:', error);
        this.modalPopupService.openPopup<AlertBoxComponent>(AlertBoxComponent, {
          message: error.error?.message || 'Error fetching appointments',
          title: 'Error',
          icon: 'fas fa-close'
        });
      }
    });
  }

  onClear() {
    this.reportForm.reset();
    this.appointmentsList = [];
    this.showTable = false;
    this.facultyList = [];
    this.courseList = [];
    this.filteredCourseList = [];
    this.examList = [];
    this.filteredExamList = []; 
    this.collegeList = [];
    this.examCache.clear();
    this.reportForm.patchValue({
      Course: [],
      Exam: [],
      FacultyId: [],
      CourseLevel: [],    
      ApptType: 'R'
    });
    this.modalPopupService.openPopup<AlertBoxComponent>(AlertBoxComponent, {
      message: 'Form cleared successfully',
      title: 'Success',
      icon: 'fas fa-check'
    });
  }


  private resetDependentFields(fields: string[]) {
    fields.forEach(field => {
      if (field === 'Course' || field === 'Exam') {
        this.reportForm.get(field)?.setValue([]);
      } else if (field === 'FacultyId') {
        this.reportForm.get(field)?.setValue([]);
      } else if (field === 'CourseLevel') {
        this.reportForm.get(field)?.setValue([]);
      } else {
        this.reportForm.get(field)?.setValue('');
      }
      this.reportForm.get(field)?.updateValueAndValidity();
    });
  }

  onPrintLetter() {
    const appointmentId = this.appointmentsList[0]?.AppointmentId;
    const selectedRole = this.reportForm.get('Role')?.value;
    
    if (!appointmentId) {
      this.modalPopupService.openPopup<AlertBoxComponent>(AlertBoxComponent, {
        message: 'No appointments found to print',
        title: 'Information',
        icon: 'fas fa-info-circle'
      });
      return;
    }

    let reportType = 'CO_LETTER';
    if (selectedRole === 'CI') {
      reportType = 'CI_LETTER';
    }
    if (this.appointmentsList[0]?.RoleCode === 'CI') {
      reportType = 'CI_LETTER';
    }

    this.spinner.show();

    const payload = { 
      ReportType: reportType,  
      AppointmentId: appointmentId 
    };

    console.log('Printing with payload:', payload);

    this.umsService.renderHbsReport(payload).subscribe({
      next: (blob: Blob) => {
        this.spinner.hide();
        console.log(' Received blob:', blob);
        console.log(' Blob type:', blob.type);
        
        if (blob.type !== 'application/pdf') {
          const reader = new FileReader();
          reader.onload = () => {
            try {
              const errorMsg = JSON.parse(reader.result as string);
              this.modalPopupService.openPopup<AlertBoxComponent>(AlertBoxComponent, {
                message: errorMsg.message || 'Error generating letter',
                title: 'Error',
                icon: 'fas fa-close'
              });
            } catch {
              this.modalPopupService.openPopup<AlertBoxComponent>(AlertBoxComponent, {
                message: 'Error generating PDF document',
                title: 'Error',
                icon: 'fas fa-close'
              });
            }
          };
          reader.readAsText(blob);
          return;
        }
        
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${reportType}_Appointment_Letter_${appointmentId}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);

        this.modalPopupService.openPopup<AlertBoxComponent>(AlertBoxComponent, {
          message: 'Letter downloaded successfully!',
          title: 'Success',
          icon: 'fas fa-check'
        });
      },
      error: (err) => {
        this.spinner.hide();
        console.error('ull error:', err);
        console.error('Error status:', err.status);
        console.error(' Error message:', err.message);
        console.error(' Error error:', err.error);
        
        let errorMessage = 'Error generating letter. Please try again.';
        if (err.status === 404) {
          errorMessage = 'Report generation service is not available. Please contact support.';
        } else if (err.status === 500) {
          errorMessage = 'Server error while generating letter. Please try again later.';
        } else if (err.error?.message) {
          errorMessage = err.error.message;
        }
        
        this.modalPopupService.openPopup<AlertBoxComponent>(AlertBoxComponent, {
          message: errorMessage,
          title: 'Error',
          icon: 'fas fa-close'
        });
      }
    });
  }

  get f() { 
    return this.reportForm.controls; 
  }
}